import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { GraduationCap, Sparkles, Award, ZoomIn, X } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { EXPERTISE, EXPERTISE_IMAGES, CONTACT, SEMINARS, QUOTE, MISSION } from "../data/site";

export const About = () => {
    const [lightbox, setLightbox] = useState(null);

    return (
        <section id="about" data-testid="about-section" className="relative py-28 lg:py-36 px-6">
            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-start">
                <Reveal className="relative">
                    <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-[0_0_60px_rgba(201,169,98,0.12)]">
                        <img
                            src="/assets/maitrii.png"
                            alt="Maitrii Shahh — Certified Graphologist & Numerologist"
                            data-testid="about-portrait"
                            className="w-full h-[520px] object-cover object-top"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#081226]/80 via-transparent to-transparent" />
                        <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
                            <div>
                                <p className="font-display text-2xl font-bold text-[#F0EBE0]">Maitrii Shahh</p>
                                <p className="text-xs uppercase tracking-[0.2em] text-[#C9A962]">Founder, Ink of Destiny</p>
                            </div>
                            <span className="text-right">
                                <span className="font-mono-num text-2xl text-[#C9A962] block leading-none">8+</span>
                                <span className="text-[9px] uppercase tracking-[0.2em] text-[#C9A962]/70">Years of Experience</span>
                            </span>
                        </div>
                    </div>
                    <div className="absolute -top-4 -left-4 w-24 h-24 border-t border-l border-[#C9A962]/30 rounded-tl-2xl pointer-events-none" />
                    <div className="absolute -bottom-4 -right-4 w-24 h-24 border-b border-r border-[#C9A962]/30 rounded-br-2xl pointer-events-none" />
                </Reveal>

                <div>
                    <Reveal>
                        <Overline>About the Graphologist</Overline>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#F0EBE0] leading-tight">
                            The hand behind <span className="italic text-[#C9A962]">the ink</span>
                        </h2>
                        <p className="mt-6 text-base lg:text-lg font-light text-[#A19D94] leading-relaxed">
                            A certified Graphologist and Numerologist with over 8 years of practice,
                            Maitrii has guided 3,500+ souls, mentored 350+ students and counselled 8 Jain
                            monks — all through the quiet language of handwriting and numbers. Currently
                            pursuing her PhD in Graphology, she blends scientific analysis with intuitive
                            healing to help people rewrite their stories.
                        </p>
                    </Reveal>

                    <Reveal delay={0.15}>
                        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
                            {[
                                { icon: GraduationCap, label: "Certified Graphologist & Numerologist" },
                                { icon: Sparkles, label: "PhD in Graphology (Pursuing)" },
                                { icon: Award, label: "8+ Years of Healing Practice" },
                            ].map(({ icon: Icon, label }, i) => (
                                <div key={i} className="rounded-xl border border-white/10 bg-[#0E1B33] p-4 flex flex-col gap-3 hover:border-[#C9A962]/30 transition-colors">
                                    <Icon size={18} className="text-[#C9A962]" />
                                    <span className="text-xs text-[#A19D94] leading-relaxed">{label}</span>
                                </div>
                            ))}
                        </div>
                    </Reveal>

                    <Reveal delay={0.25}>
                        <p className="mt-8 text-xs uppercase tracking-[0.25em] text-[#5C5A56] mb-4">Areas of Expertise</p>
                        <div className="flex flex-wrap gap-2">
                            {EXPERTISE.map((e) => (
                                EXPERTISE_IMAGES[e] ? (
                                    <button key={e} data-testid={`expertise-${e.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                        onClick={() => setLightbox({ title: e, img: EXPERTISE_IMAGES[e] })}
                                        className="px-4 py-2 rounded-full border border-[#C9A962]/40 bg-[#C9A962]/5 text-xs text-[#C9A962] hover:bg-[#C9A962]/15 transition-colors flex items-center gap-2">
                                        {e} <ZoomIn size={12} />
                                    </button>
                                ) : (
                                    <span key={e} className="px-4 py-2 rounded-full border border-white/10 text-xs text-[#A19D94] hover:border-[#C9A962]/40 hover:text-[#C9A962] transition-colors">
                                        {e}
                                    </span>
                                )
                            ))}
                        </div>
                        <p className="mt-3 text-[11px] text-[#5C5A56]">Tap a highlighted expertise to view a glimpse.</p>
                        <blockquote data-testid="about-quote" className="mt-8 border-l-2 border-[#C9A962]/50 pl-5 py-1">
                            <p className="font-display italic text-lg text-[#F0EBE0] leading-relaxed">“{QUOTE}”</p>
                        </blockquote>
                        <p className="mt-5 text-sm font-light text-[#A19D94] leading-relaxed">
                            <span className="text-[#C9A962] uppercase tracking-[0.2em] text-xs block mb-2">My Mission</span>
                            {MISSION}
                        </p>
                        <p className="mt-8 text-xs uppercase tracking-[0.25em] text-[#5C5A56] mb-4">Seminars & Associations</p>
                        <div className="flex flex-wrap gap-2">
                            {SEMINARS.map((s) => (
                                <span key={s} className="px-4 py-2 rounded-full border border-[#C9A962]/20 bg-[#C9A962]/5 text-xs text-[#C9A962]/90">
                                    {s}
                                </span>
                            ))}
                        </div>
                        <motion.a
                            whileHover={{ x: 6 }}
                            href={CONTACT.instagram}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="about-know-more-btn"
                            className="inline-block mt-8 text-sm uppercase tracking-[0.2em] text-[#C9A962] border-b border-[#C9A962]/40 pb-1 hover:border-[#C9A962] transition-colors"
                        >
                            Know More →
                        </motion.a>
                        <div data-testid="about-working-photos" className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-2xl">
                            <figure>
                                <div className="rounded-2xl overflow-hidden border border-white/10 shadow-[0_0_50px_rgba(201,169,98,0.1)]">
                                    <img src="/assets/gallery/consultation.jpg" alt="Maitrii in a one-on-one consultation session" className="w-full h-64 object-cover" />
                                </div>
                                <figcaption className="mt-3 text-xs uppercase tracking-[0.2em] text-[#5C5A56]">At Work · One-on-One Consultation</figcaption>
                            </figure>
                            <figure>
                                <div className="rounded-2xl overflow-hidden border border-white/10 shadow-[0_0_50px_rgba(201,169,98,0.1)]">
                                    <img src="/assets/gallery/jbn-event.jpg" alt="Maitrii Shahh at JBN 360 Pune — JITO Business Network" className="w-full h-64 object-cover object-top" />
                                </div>
                                <figcaption className="mt-3 text-xs uppercase tracking-[0.2em] text-[#5C5A56]">At Work · JBN 360 Pune — JITO Business Network</figcaption>
                            </figure>
                        </div>
                    </Reveal>
                </div>
            </div>

            <AnimatePresence>
                {lightbox && (
                    <motion.div
                        data-testid="expertise-lightbox"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setLightbox(null)}
                        className="fixed inset-0 z-[70] flex items-center justify-center p-6 bg-black/80 backdrop-blur-sm"
                    >
                        <motion.figure
                            initial={{ opacity: 0, scale: 0.92 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.92 }}
                            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                            onClick={(e) => e.stopPropagation()}
                            className="relative max-w-lg w-full rounded-2xl overflow-hidden border border-[#C9A962]/30 bg-[#0E1B33] shadow-[0_0_80px_rgba(201,169,98,0.2)]"
                        >
                            <img src={lightbox.img} alt={lightbox.title} className="w-full object-cover max-h-[65vh]" />
                            <figcaption className="px-6 py-4 flex items-center justify-between">
                                <span data-testid="lightbox-title" className="font-display text-lg font-semibold text-[#F0EBE0]">{lightbox.title}</span>
                                <button data-testid="lightbox-close" onClick={() => setLightbox(null)} aria-label="Close"
                                    className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center text-[#A19D94] hover:text-[#C9A962] hover:border-[#C9A962]/50 transition-colors">
                                    <X size={16} />
                                </button>
                            </figcaption>
                        </motion.figure>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
};
